import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { OrderStatus } from "@/api/lib/db/types";
import Icon from "../../../lib/components/custom/Icon";
import { Badge } from "../../../lib/components/ui/badge";
import { Button, buttonVariants } from "../../../lib/components/ui/button";
import { Card } from "../../../lib/components/ui/card";
import { Dialog } from "../../../lib/components/ui/dialog";
import { Skeleton } from "../../../lib/components/ui/skeleton";
import { useApi } from "../../../lib/hooks/api/use-api";
import { useAdminStore } from "../../../lib/hooks/use-store";
import { cn } from "../../../lib/utils";
import AdminOrderCard from "../AdminOrderCard";
import AdminLayout from "../layout";
import OrderDetailsDialog from "../OrderDetailsDialog";
import PrintableOrder from "../PrintableOrder";

const fadeInUp = {
	initial: { opacity: 0, y: 10 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.4, ease: "easeOut" },
};

const staggerContainer = {
	animate: {
		transition: {
			staggerChildren: 0.05,
		},
	},
};

export default function AdminDashboard() {
	const [confirmingOrder, setConfirmingOrder] = useState<number | null>(null);
	const [openDialogs, setOpenDialogs] = useState<Set<number>>(new Set());
	const [searchTerm, setSearchTerm] = useState("");
	const [sortBy, setSortBy] = useState<"newest" | "oldest">("newest");
	const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
	const { analytics, orders } = useApi();
	const { showTodayOnly, setShowTodayOnly } = useAdminStore();
	const { t } = useTranslation();

	// Fetch analytics data
	const {
		data: analyticsData,
		isLoading: analyticsLoading,
		error: analyticsError,
	} = analytics.getDashboard();

	// Fetch ongoing orders (all orders that are not delivered or cancelled)
	// SSE auto-refresh is handled in AdminLayout
	const {
		data: ordersData,
		isLoading: ordersLoading,
		error: ordersError,
		refetch,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = orders.getAllInfinite({ limit: 10 });

	const ongoingOrders = useMemo(() => {
		if (!ordersData?.pages) return [];
		// Flatten all pages and filter for received orders
		const allOrders = ordersData.pages.flatMap((page) => page.orders || []);

		// Filter orders
		const filtered = allOrders.filter((order) => {
			// Filter by status (only received orders)
			if (order.status !== "received") return false;

			// Filter by today's orders if toggle is on
			if (showTodayOnly) {
				const today = new Date();
				const todayStart = new Date(
					today.getFullYear(),
					today.getMonth(),
					today.getDate(),
				);
				const orderDate = new Date(order.createdAt);
				if (orderDate < todayStart) return false;
			}

			// Filter by search term
			const matchesSearch =
				order.id.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
				(order.user?.name || "")
					.toLowerCase()
					.includes(searchTerm.toLowerCase()) ||
				(order.user?.email || "")
					.toLowerCase()
					.includes(searchTerm.toLowerCase());

			return matchesSearch;
		});

		// Sort orders
		filtered.sort((a, b) => {
			// Scheduled orders come first, sorted by scheduledFor time (earliest first)
			const aScheduled = a.scheduledFor
				? new Date(a.scheduledFor).getTime()
				: null;
			const bScheduled = b.scheduledFor
				? new Date(b.scheduledFor).getTime()
				: null;

			// If one is scheduled and the other isn't, scheduled comes first
			if (aScheduled && !bScheduled) return -1;
			if (!aScheduled && bScheduled) return 1;

			// If both are scheduled, sort by scheduledFor time (earliest first)
			if (aScheduled && bScheduled) {
				return aScheduled - bScheduled;
			}

			// Non-scheduled orders sorted by createdAt
			if (sortBy === "newest") {
				return (
					new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
				);
			} else {
				return (
					new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
				);
			}
		});

		return filtered;
	}, [ordersData, searchTerm, sortBy, showTodayOnly]);

	const loadMore = () => {
		if (hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	};

	const getStatusColor = (status: string) => {
		if (status === "pending") return "bg-red-100 text-red-800 border-red-200";
		switch (status) {
			case "received":
				return "bg-destructive/10 text-destructive border-destructive/20";
			case "confirmed":
				return "bg-blue-100 text-blue-800 border-blue-200";
			case "preparing":
				return "bg-orange-100 text-orange-800 border-orange-200";
			case "ready":
				return "bg-green-100 text-green-800 border-green-200";
			case "out-for-delivery":
				return "bg-purple-100 text-purple-800 border-purple-200";
			case "delivered":
				return "bg-gray-100 text-gray-800 border-gray-200";
			case "cancelled":
				return "bg-red-100 text-red-800 border-red-200";
			default:
				return "bg-gray-100 text-gray-800 border-gray-200";
		}
	};

	const getStatusText = (status: string) => {
		if (status === "pending") return t("admin.dashboard.status.pending_unpaid");
		switch (status) {
			case "received":
				return t("admin.dashboard.status.new_order");
			case "confirmed":
				return t("admin.dashboard.status.confirmed");
			case "preparing":
				return t("admin.dashboard.status.preparing");
			case "ready":
				return t("admin.dashboard.status.ready");
			case "out-for-delivery":
				return t("admin.dashboard.status.out_for_delivery");
			case "delivered":
				return t("admin.dashboard.status.delivered");
			case "cancelled":
				return t("admin.dashboard.status.cancelled");
			default:
				return status;
		}
	};

	const formatTime = (dateString: string) => {
		return new Date(dateString).toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const formatDateTime = (dateString: string) => {
		return new Date(dateString).toLocaleString([], {
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const handleStatusUpdate = async (
		orderId: number,
		newStatus: OrderStatus,
	) => {
		setConfirmingOrder(orderId);
		try {
			await orders.updateStatus.mutateAsync({
				orderId: orderId.toString(),
				status: newStatus,
			});
			toast.success(
				t("admin.dashboard.toast.status_updated", {
					status: getStatusText(newStatus),
				}),
			);
			refetch();

			// Close dialog if order was moved to preparing (confirmed)
			if (newStatus === "preparing") {
				setOpenDialogs((prev) => {
					const newSet = new Set(prev);
					newSet.delete(orderId);
					return newSet;
				});
			}
		} catch (error) {
			console.error("Failed to update order status:", error);
			toast.error(t("admin.dashboard.toast.update_failed"));
		} finally {
			setConfirmingOrder(null);
		}
	};

	// Error handling
	if (analyticsError || ordersError) {
		return (
			<AdminLayout>
				<div className="flex items-center justify-center min-h-[400px]">
					<div className="text-center">
						<div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Icon name="X" className="w-6 h-6 text-destructive" />
						</div>
						<h3 className="text-lg font-semibold mb-2">
							{t("admin.dashboard.error.title")}
						</h3>
						<p className="text-muted-foreground mb-4">
							{analyticsError?.message ||
								ordersError?.message ||
								t("admin.dashboard.error.description")}
						</p>
						<Button onClick={() => window.location.reload()}>
							{t("admin.dashboard.error.retry")}
						</Button>
					</div>
				</div>
			</AdminLayout>
		);
	}

	return (
		<AdminLayout>
			<div className="space-y-6">
				{/* Minimal Header & Analytics Strip */}
				<div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-2 border-b border-border/40">
					<motion.div
						initial={{ opacity: 0, x: -10 }}
						animate={{ opacity: 1, x: 0 }}
						className="flex flex-col gap-1"
					>
						<h1 className="text-2xl font-bold tracking-tight text-foreground">
							{t("admin.dashboard.title")}
						</h1>
						<p className="text-sm text-muted-foreground">
							{t("admin.dashboard.subtitle")}
						</p>
					</motion.div>

					{/* Minimal Analytics Stats */}
					<motion.div
						variants={staggerContainer}
						initial="initial"
						animate="animate"
						className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-10 overflow-x-auto pb-1 sm:pb-0 hide-scrollbar"
					>
						{/* Total Orders */}
						<Link to="/admin/orders">
							<motion.div
								variants={fadeInUp}
								className="group flex items-center gap-3 pr-6 border-r border-border/60 last:border-0 last:pr-0 cursor-pointer"
							>
								<div className="bg-primary/10 p-2 rounded-lg transition-colors">
									<Icon
										name="Package"
										className="size-6 text-muted-foreground group-hover:text-primary transition-colors"
									/>
								</div>
								<div>
									<p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
										{t("admin.dashboard.analytics.orders")}
									</p>
									<div className="text-xl font-bold text-foreground">
										{analyticsLoading ? (
											<Skeleton className="h-6 w-8" />
										) : (
											analyticsData?.today?.orders || 0
										)}
									</div>
								</div>
							</motion.div>
						</Link>

						{/* Revenue */}
						<Link to="/admin/orders">
							<motion.div
								variants={fadeInUp}
								className="group flex items-center gap-3 pr-6 border-r border-border/60 last:border-0 last:pr-0 cursor-pointer"
							>
								<div className="bg-primary/10 p-2 rounded-lg transition-colors">
									<Icon
										name="DollarSign"
										className="size-6 text-muted-foreground group-hover:text-primary transition-colors"
									/>
								</div>
								<div>
									<p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
										{t("admin.dashboard.analytics.revenue")}
									</p>
									<div className="text-xl font-bold text-foreground">
										$
										{analyticsLoading ? (
											<Skeleton className="h-6 w-12" />
										) : (
											(analyticsData?.today?.revenue || 0).toFixed(0)
										)}
									</div>
								</div>
							</motion.div>
						</Link>

						{/* Pending */}
						<Link to="/admin/orders">
							<motion.div
								variants={fadeInUp}
								className="group flex items-center gap-3 pr-6 last:border-0 last:pr-0 cursor-pointer"
							>
								<div className="bg-primary/10 p-2 rounded-lg transition-colors">
									<Icon
										name="Clock"
										className="size-6 text-muted-foreground group-hover:text-primary transition-colors"
									/>
								</div>
								<div>
									<p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
										{t("admin.dashboard.analytics.pending")}
									</p>
									<div className="text-xl font-bold text-foreground">
										{analyticsLoading ? (
											<Skeleton className="h-6 w-6" />
										) : (
											analyticsData?.today?.pendingOrders || 0
										)}
									</div>
								</div>
							</motion.div>
						</Link>
					</motion.div>
				</div>

				{/* Incoming Orders - Takes most space */}
				<section className="flex-1 min-h-[500px]">
					<div className="flex flex-col gap-4 mb-4">
						<div className="flex items-center justify-between">
							<motion.h3
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.4, ease: "easeOut" }}
								className="hidden md:block"
							>
								{t("admin.dashboard.incoming_orders.title")}
							</motion.h3>
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								className="flex items-center gap-3"
							>
								{ongoingOrders.length > 0 && (
									<Button
										variant="outline"
										size="sm"
										className="gap-2"
										onClick={() => window.print()}
									>
										<Icon name="Printer" className="w-4 h-4" />
										{t("admin.dashboard.incoming_orders.print_all")}
									</Button>
								)}
								<Button
									variant="ghost"
									size="sm"
									className="gap-2 text-muted-foreground hover:text-foreground"
									asChild
								>
									<Link to="/admin/orders">
										{t("admin.dashboard.incoming_orders.view_history")}
										<Icon name="ArrowRight" className="w-4 h-4" />
									</Link>
								</Button>
							</motion.div>
						</div>
					</div>

					<div className="flex items-center justify-between mb-4">
						<span className="text-sm text-muted-foreground">
							{t("admin.orders.showing_orders", {
								count: ongoingOrders.length,
							})}
							{ordersLoading && (
								<span className="inline-flex items-center gap-2 ml-2">
									<Icon name="Loader" className="w-3 h-3 animate-spin" />{" "}
									{t("admin.orders.updating")}
								</span>
							)}
						</span>
					</div>

					<motion.div
						variants={staggerContainer}
						initial="initial"
						animate="animate"
						className={cn(
							viewMode === "grid"
								? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
								: "flex flex-col gap-3",
						)}
						key={`${searchTerm}-${sortBy}-${showTodayOnly}-${ongoingOrders.length}`}
					>
						{ordersLoading ? (
							Array.from({ length: 4 }).map((_, i) => (
								<Card key={i} className="p-4 bg-muted/30 border-none">
									<div className="space-y-3">
										<div className="flex items-center gap-3">
											<Skeleton className="w-10 h-10 rounded-full" />
											<div className="space-y-1.5 flex-1">
												<Skeleton className="h-4 w-24" />
												<Skeleton className="h-3 w-32" />
											</div>
										</div>
										<Skeleton className="h-16 w-full rounded-md" />
									</div>
								</Card>
							))
						) : ongoingOrders.length === 0 ? (
							<motion.div
								initial={{ opacity: 0, scale: 0.98 }}
								animate={{ opacity: 1, scale: 1 }}
								className={cn(
									viewMode === "grid" ? "col-span-full" : "",
									"py-20",
								)}
							>
								<div className="flex flex-col items-center justify-center text-center">
									<div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
										<Icon
											name="Check"
											className="size-20 text-muted-foreground"
										/>
									</div>
									<h3 className="text-lg font-medium text-foreground">
										{t("admin.dashboard.incoming_orders.empty_title")}
									</h3>
									<p className="text-muted-foreground mt-2 max-w-md">
										{t("admin.dashboard.incoming_orders.empty_description")}
									</p>
								</div>
							</motion.div>
						) : (
							<>
								<AnimatePresence mode="popLayout" initial={false}>
									{ongoingOrders.map((order) => (
										<Dialog
											key={order.id}
											open={openDialogs.has(order.id)}
											onOpenChange={(open) => {
												setOpenDialogs((prev) => {
													const newSet = new Set(prev);
													if (open) {
														newSet.add(order.id);
													} else {
														newSet.delete(order.id);
													}
													return newSet;
												});
											}}
										>
											<motion.div
												variants={fadeInUp}
												whileHover={{
													y: viewMode === "grid" ? -2 : -1,
													transition: { duration: 0.2 },
												}}
												className={cn(viewMode === "grid" ? "h-full" : "")}
												layout
											>
												{viewMode === "grid" ? (
													<Card
														className="h-full cursor-pointer hover:border-primary/50 hover:shadow-sm transition-all duration-300 group bg-card"
														onClick={() => {
															setOpenDialogs((prev) =>
																new Set(prev).add(order.id),
															);
														}}
													>
														<AdminOrderCard
															orderId={order.id}
															initialOrderData={order}
															getStatusColor={getStatusColor}
															getStatusText={getStatusText}
															formatDateTime={formatTime}
														/>
													</Card>
												) : (
													<div
														className="p-4 cursor-pointer bg-card rounded-xl border shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-300 flex items-center gap-4"
														onClick={() => {
															setOpenDialogs((prev) =>
																new Set(prev).add(order.id),
															);
														}}
													>
														<div className="shrink-0 w-16 h-16 bg-muted/50 rounded-lg flex flex-col items-center justify-center border text-center">
															<span className="text-xs text-muted-foreground font-mono">
																#{order.id}
															</span>
														</div>

														<div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
															<div className="md:col-span-1">
																<h3 className="font-semibold text-base truncate">
																	{order.user?.name ||
																		t("admin.order_card.unknown_customer")}
																</h3>
																<p className="text-sm text-muted-foreground truncate">
																	{order.user?.email}
																</p>
															</div>

															<div className="md:col-span-1">
																<div className="flex items-center gap-2 text-sm text-muted-foreground">
																	<Icon
																		name="MapPin"
																		className="w-4 h-4 shrink-0"
																	/>
																	<span className="truncate max-w-[200px]">
																		{order.address?.streetAddress}
																	</span>
																</div>
																<div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
																	<Icon
																		name={
																			order.scheduledFor ? "Calendar" : "Clock"
																		}
																		className="w-3 h-3 shrink-0"
																	/>
																	{order.scheduledFor
																		? (() => {
																				try {
																					const scheduledDate = new Date(
																						order.scheduledFor,
																					);
																					if (
																						Number.isNaN(
																							scheduledDate.getTime(),
																						)
																					)
																						return formatDateTime(
																							order.createdAt,
																						);
																					return `Scheduled: ${scheduledDate.toLocaleTimeString(
																						[],
																						{
																							hour: "numeric",
																							minute: "2-digit",
																							hour12: true,
																						},
																					)}`;
																				} catch {
																					return formatDateTime(
																						order.createdAt,
																					);
																				}
																			})()
																		: formatDateTime(order.createdAt)}
																</div>
															</div>

															<div className="md:col-span-1 flex items-center justify-start md:justify-center gap-2 flex-wrap">
																{order.scheduledFor &&
																	(() => {
																		try {
																			const scheduledDate = new Date(
																				order.scheduledFor,
																			);
																			if (Number.isNaN(scheduledDate.getTime()))
																				return null;
																			return (
																				<Badge
																					variant="outline"
																					className="text-xs font-medium px-2 py-0.5 bg-secondary/10 text-secondary border-secondary/20"
																				>
																					<Icon
																						name="Clock"
																						className="w-3 h-3 mr-1"
																					/>
																					{scheduledDate.toLocaleTimeString(
																						[],
																						{
																							hour: "numeric",
																							minute: "2-digit",
																							hour12: true,
																						},
																					)}
																				</Badge>
																			);
																		} catch {
																			return null;
																		}
																	})()}
																<Badge
																	className={cn(
																		"text-xs font-medium px-2.5 py-1",
																		getStatusColor(order.status),
																	)}
																>
																	{getStatusText(order.status)}
																</Badge>
															</div>

															<div className="md:col-span-1 text-right">
																<p className="font-bold text-lg text-primary">
																	€{Number(order.totalPrice).toFixed(2)}
																</p>
																<p
																	className={`text-xs font-medium ${order.paymentMethod === "cash" ? "text-amber-600" : "text-blue-600"}`}
																>
																	{order.paymentMethod === "cash"
																		? t("admin.order_card.cash_on_delivery")
																		: t("admin.order_card.card")}
																</p>
															</div>
														</div>

														<div className="shrink-0 ml-2">
															<Button
																variant="ghost"
																size="icon"
																className="text-muted-foreground hover:text-primary"
															>
																<Icon name="ChevronRight" className="w-5 h-5" />
															</Button>
														</div>
													</div>
												)}

												<OrderDetailsDialog
													orderId={order.id}
													onStatusUpdate={handleStatusUpdate}
													confirmingOrder={confirmingOrder}
												/>
											</motion.div>
										</Dialog>
									))}
								</AnimatePresence>

								{hasNextPage && (
									<motion.div
										className="col-span-full flex justify-center mt-4"
										initial={{ opacity: 0, y: 10 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ delay: 0.2 }}
									>
										<motion.button
											onClick={loadMore}
											className={cn(buttonVariants({ variant: "primary" }))}
											disabled={isFetchingNextPage}
											whileHover={{ y: -2 }}
											whileTap={{ y: 1 }}
										>
											{isFetchingNextPage ? (
												<>
													<Icon name="Loader" className="size-4 animate-spin" />
													{t("common.loading")}
												</>
											) : (
												<>
													<Icon name="ArrowDown" className="size-4" />
													{t("home.browse.load_more")}
												</>
											)}
										</motion.button>
									</motion.div>
								)}
							</>
						)}
					</motion.div>
				</section>
			</div>

			{/* Hidden container for printing all new orders */}
			<div className="hidden print:block print-container">
				{ongoingOrders.map((order) => (
					<PrintableOrder key={order.id} order={order} />
				))}
			</div>
		</AdminLayout>
	);
}
